import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { CardContainer, LoadingSpinner, StatusChip } from "@msys/ui";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { BreadcrumbItem, Page } from "../../../../commons/layout/Page.js";
import { PageContainer } from "../../../../commons/layout/PageContainer.js";
import type { IntegrationDashboardProps } from "../../integrationRegistry.js";
import { TierCard } from "./TierCard.js";
import { ActivityFeed } from "./ActivityFeed.js";
import { useIntegrationErrorMessage } from "../../integrationErrorMessage.js";
import {
  INTEGRATIONS_BASE_PATH,
  integrationDashboardPath,
  integrationSettingsPath,
} from "../../paths.js";
import {
  usePdsSyncDashboardStatusQuery,
  usePdsSyncTriggerHeartbeatMutation,
  usePdsSyncTriggerTier1Mutation,
  usePdsSyncCancelTier1Mutation,
  usePdsSyncTriggerFullSyncMutation,
  usePdsSyncCancelFullSyncMutation,
  usePdsSyncPauseMutation,
  usePdsSyncResumeMutation,
} from "./PdsSyncDashboard.generated.js";

const DEFAULT_POLL_INTERVAL = 10_000;
const ACTIVE_POLL_INTERVAL = 5_000;

export const PdsSyncDashboard = ({
  integrationId,
}: IntegrationDashboardProps) => {
  const { t } = useTranslate([
    "Integrations",
    "OrganisationPageTopbar",
    "Global",
  ]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();

  const [pendingHeartbeat, setPendingHeartbeat] = React.useState(false);
  const [pendingTier1, setPendingTier1] = React.useState(false);
  const [pendingFullSync, setPendingFullSync] = React.useState(false);
  // MEIS-13196 (design D5): the Cancel buttons get the same pending
  // treatment the trigger buttons have always had. Tracked separately from
  // pendingTier1/pendingFullSync because they clear on the OPPOSITE
  // condition — a trigger is done when the card starts running, a cancel
  // when it stops.
  const [pendingCancelTier1, setPendingCancelTier1] = React.useState(false);
  const [pendingCancelFullSync, setPendingCancelFullSync] =
    React.useState(false);
  const [confirmFullSyncOpen, setConfirmFullSyncOpen] = React.useState(false);
  const [confirmPauseToggleOpen, setConfirmPauseToggleOpen] =
    React.useState(false);

  const isActiveOp =
    pendingHeartbeat ||
    pendingTier1 ||
    pendingFullSync ||
    pendingCancelTier1 ||
    pendingCancelFullSync;

  const pollInterval = isActiveOp
    ? ACTIVE_POLL_INTERVAL
    : DEFAULT_POLL_INTERVAL;

  const { data, loading, refetch } = usePdsSyncDashboardStatusQuery({
    client,
    pollInterval,
    fetchPolicy: "cache-and-network",
  });

  const pdsSyncStatus = getDataOrNull(data?.pdsSyncStatus);
  const ws = pdsSyncStatus?.workflowState;
  const workflowStatus = pdsSyncStatus?.workflowStatus ?? null;
  const health = pdsSyncStatus?.health;
  const events = pdsSyncStatus?.events ?? [];

  React.useEffect(() => {
    if (pendingHeartbeat && ws?.heartbeatCalls && ws.heartbeatCalls.length > 0)
      setPendingHeartbeat(false);
    if (ws?.tier1Running && pendingTier1) setPendingTier1(false);
    if (ws?.fullSyncRunning && pendingFullSync) setPendingFullSync(false);
    // MEIS-13196: the mirror of the two rules above. Explicitly `=== false`
    // rather than falsy — while `ws` is undefined (no workflow state yet)
    // nothing is known about the card, and clearing then would drop the
    // acknowledgement the user just got.
    if (ws?.tier1Running === false && pendingCancelTier1)
      setPendingCancelTier1(false);
    if (ws?.fullSyncRunning === false && pendingCancelFullSync)
      setPendingCancelFullSync(false);
    // Copilot review round 5: the two rules above need live workflow state to
    // fire, and an orchestrator that fails or is terminated right after the
    // cancel mutation succeeds never produces any — `ws` stays null, so the
    // acknowledgement would never clear and the Cancel button would stay
    // disabled for as long as the dashboard is open. A status that is known
    // and is not RUNNING says the orchestrator is definitively not running,
    // which is the outcome the pending cancel was waiting for. `null` (status
    // unknown) and RUNNING are deliberately excluded: neither is evidence,
    // and clearing on them would drop the acknowledgement mid-flight.
    if (
      workflowStatus !== null &&
      workflowStatus !== "RUNNING" &&
      (pendingCancelTier1 || pendingCancelFullSync)
    ) {
      setPendingCancelTier1(false);
      setPendingCancelFullSync(false);
    }
  }, [
    ws?.heartbeatCalls,
    ws?.tier1Running,
    ws?.fullSyncRunning,
    workflowStatus,
    pendingHeartbeat,
    pendingTier1,
    pendingFullSync,
    pendingCancelTier1,
    pendingCancelFullSync,
  ]);

  const [triggerHeartbeat] = usePdsSyncTriggerHeartbeatMutation({
    client,
    context: { noErrorNotification: true },
  });
  const [triggerTier1] = usePdsSyncTriggerTier1Mutation({
    client,
    context: { noErrorNotification: true },
  });
  const [cancelTier1] = usePdsSyncCancelTier1Mutation({
    client,
    context: { noErrorNotification: true },
  });
  const [triggerFullSync] = usePdsSyncTriggerFullSyncMutation({
    client,
    context: { noErrorNotification: true },
  });
  const [cancelFullSync] = usePdsSyncCancelFullSyncMutation({
    client,
    context: { noErrorNotification: true },
  });
  const [pause] = usePdsSyncPauseMutation({
    client,
    context: { noErrorNotification: true },
  });
  const [resume] = usePdsSyncResumeMutation({
    client,
    context: { noErrorNotification: true },
  });

  const fallbackError = t("Something went wrong. Please try again.", {
    ns: "Global",
  });
  const getErrorMessage = useIntegrationErrorMessage();

  const handleMutation = React.useCallback(
    async (
      // `unknown`: the handler only awaits the result, never inspects it,
      // so nothing here needs the mutation's payload type.
      mutation: () => Promise<unknown>,
      setPending?: (v: boolean) => void,
      // MEIS-13196 (design D5): opt-in refetch for the cancel buttons, so
      // the card flips to Idle as soon as the mutation resolves instead of
      // waiting out the poll. An option here rather than a `refetch()` at
      // each call site, so the success path stays in one place.
      options?: { refetchAfter?: boolean }
    ) => {
      try {
        setPending?.(true);
        await mutation();
        if (options?.refetchAfter) {
          // The mutation already succeeded — a failed refetch must not be
          // reported as a failed cancel, and the poll catches up anyway.
          await refetch().catch(() => {});
        }
      } catch (e) {
        setPending?.(false);
        enqueueSnackbar(getErrorMessage(e, fallbackError), {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, fallbackError, getErrorMessage, refetch]
  );

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("Organisation", { ns: "OrganisationPageTopbar" }),
      to: "/organisation",
    },
    { title: t("Integrations"), to: INTEGRATIONS_BASE_PATH },
    { title: "PDS", to: integrationSettingsPath(integrationId) },
    { title: t("Dashboard"), to: integrationDashboardPath(integrationId) },
  ];

  if (loading && !data) return <LoadingSpinner />;

  const isPaused = ws?.paused ?? false;
  const isInitializing = ws && ws.totalHitCount === 0;
  // No live workflow state — the orchestrator isn't running right now.
  // Nothing supervises or restarts it; recovery is the user's click, which
  // the revive-on-trigger mutations (signalWithStart) make immediate:
  // controls that request work stay enabled, only running-only controls are
  // disabled (MEIS-13089, D2/D3).
  const isWorkflowDown = !ws;
  // Only these two statuses warrant the "stopped" warning banner — an
  // orchestrator that was simply never started yet (NOT_FOUND, e.g. right
  // after connecting) or whose status couldn't be determined (null) isn't
  // truthfully described as "stopped".
  const isWorkflowFailedOrTerminated =
    workflowStatus === "FAILED" || workflowStatus === "TERMINATED";

  const totalCustomers = ws ? ws.totalHitCount : 0;
  const tier1Pct =
    totalCustomers > 0 ? ((ws?.tier1Count ?? 0) / totalCustomers) * 100 : 0;
  const tier2Count = totalCustomers - (ws?.tier1Count ?? 0);
  const tier2Pct = totalCustomers > 0 ? (tier2Count / totalCustomers) * 100 : 0;

  const apiVersionIndicator: Record<string, string> = {
    verified: "",
    open: " ?",
    failed: " !",
    unsupported: " ✕",
    unknown: "",
  };

  const apiVersionLabel = ws?.apiVersion
    ? `v${ws.apiVersion}${ws.apiVersionRevision ? ` ${ws.apiVersionRevision}` : ""}${apiVersionIndicator[ws.apiVersionStatus] ?? ""}`
    : "v?";

  const apiVersionColor: Record<string, { bg: string; text: string }> = {
    verified: { bg: "#e8f5e9", text: "#2e7d32" },
    open: { bg: "#fff3e0", text: "#e65100" },
    failed: { bg: "#fff3e0", text: "#e65100" },
    unsupported: { bg: "#ffebee", text: "#c62828" },
    unknown: { bg: "#fff3e0", text: "#e65100" },
  };

  const apiVersionTooltip: Record<string, string> = {
    verified: ws?.apiVersionRevision
      ? t("Verified API version (revision {revision})", {
          revision: ws.apiVersionRevision,
          ns: "Integrations",
        })
      : t("Verified API version"),
    open: t("API version not verified — may cause errors"),
    failed: t("API changed without a version change"),
    unsupported: t("Version not supported — sync stopped"),
    unknown: t("API version could not be determined"),
  };

  const apiStatusLabel: Record<string, string> = {
    ok: t("OK", { ns: "Global" }),
    degraded: t("Slow"),
    unreachable: t("Unreachable"),
    unknown: t("Connecting..."),
  };

  const formatRelative = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t("just now");
    if (mins < 60)
      return t("{minutes} min ago", { minutes: mins, ns: "Integrations" });
    const hrs = Math.floor(mins / 60);
    return t("{hours} h ago", { hours: hrs, ns: "Integrations" });
  };

  const isHeartbeatRunning = Boolean(
    ws?.heartbeatCalls &&
    ws.heartbeatCalls.length > 0 &&
    ws.heartbeatCalls.some(c => c.status !== "done")
  );
  const checkNowLabel = pendingHeartbeat
    ? t("Requested...")
    : isHeartbeatRunning
      ? t("Running...")
      : t("Check now");

  // Tier card stats: real numbers while the workflow is up, dashes/"Idle"
  // while it's down — the cards stay visible (not hidden) so the still-usable
  // trigger buttons remain reachable (MEIS-13089).
  const tier1Stats = ws
    ? [
        {
          label: t("Last sync"),
          value: formatRelative(ws.tier1LastCompletedAt),
        },
        {
          label: t("Interval"),
          value: `${Math.round(ws.tier1SyncIntervalS / 60)} min`,
        },
        {
          label: t("Customers", { ns: "Integrations" }),
          value:
            ws.tier1CompositionKunden != null
              ? ws.tier1CompositionKunden.toLocaleString()
              : "—",
        },
        ...(!ws.tier1Running
          ? [
              {
                label: t("Duration"),
                value:
                  ws.tier1LastCompletedAt && ws.tier1LastDurationS
                    ? `${ws.tier1LastDurationS}s`
                    : "—",
              },
              {
                label: t("Changes"),
                value: ws.tier1LastCompletedAt
                  ? `${ws.tier1LastChanges ?? 0}`
                  : "—",
                color:
                  ws.tier1LastChanges && ws.tier1LastChanges > 0
                    ? "#4caf50"
                    : undefined,
              },
            ]
          : [
              {
                label: t("Status"),
                value:
                  ws.tier1TotalBatches > 0 &&
                  ws.tier1CompletedBatches < ws.tier1TotalBatches
                    ? `${ws.tier1CompletedBatches}/${ws.tier1TotalBatches} Batches`
                    : t("Running..."),
                color: "#1565C0",
              },
            ]),
      ]
    : [
        { label: t("Last sync"), value: "—" },
        { label: t("Interval"), value: "—" },
        { label: t("Customers", { ns: "Integrations" }), value: "—" },
        { label: t("Status"), value: t("Idle") },
      ];

  const fullSyncStats = ws
    ? [
        {
          label: t("Last sync"),
          value: formatRelative(ws.fullSyncLastCompletedAt),
        },
        {
          label: t("Status"),
          value: ws.fullSyncRunning ? t("Running...") : t("Idle"),
          color: ws.fullSyncRunning ? "#1565C0" : undefined,
        },
      ]
    : [
        { label: t("Last sync"), value: "—" },
        { label: t("Status"), value: t("Idle") },
      ];

  return (
    <Page title="PDS" breadcrumbs={breadcrumbs} breadcrumbsSize="medium">
      <PageContainer>
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 640 }}>
          {/* Header */}
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <Typography variant="h6">PDS</Typography>
              {ws && (
                <StatusChip
                  label={apiStatusLabel[ws.apiStatus] ?? ws.apiStatus}
                  size="small"
                  backgroundColor={
                    ws.apiStatus === "ok" ? "#e8f5e9" : "#fff3e0"
                  }
                  textColor={ws.apiStatus === "ok" ? "#2e7d32" : "#e65100"}
                />
              )}
              {ws && (
                <Tooltip
                  title={apiVersionTooltip[ws.apiVersionStatus] ?? ""}
                  arrow
                >
                  <StatusChip
                    label={apiVersionLabel}
                    size="small"
                    backgroundColor={
                      apiVersionColor[ws.apiVersionStatus]?.bg ?? "#fff3e0"
                    }
                    textColor={
                      apiVersionColor[ws.apiVersionStatus]?.text ?? "#e65100"
                    }
                    style={{ fontFamily: "monospace" }}
                  />
                </Tooltip>
              )}
              {health?.updatedAt && (
                <Typography variant="body2" color="text.secondary">
                  {t("Last sync check", { ns: "Integrations" })}:{" "}
                  {formatRelative(health.updatedAt)}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              {/* Requests work via signalWithStart — stays enabled even
                  when the workflow is down, since clicking it revives the
                  sync (MEIS-13089, D3). */}
              <Button
                size="small"
                variant="outlined"
                disabled={isPaused || pendingHeartbeat || isHeartbeatRunning}
                onClick={() =>
                  handleMutation(() => triggerHeartbeat(), setPendingHeartbeat)
                }
              >
                {checkNowLabel}
              </Button>
            </Stack>
          </Stack>

          {/* Banners */}
          {isWorkflowFailedOrTerminated && (
            <Alert severity="error">
              <AlertTitle>{t("Synchronization stopped")}</AlertTitle>
              {t(
                "The sync is not running. You can start it again with the sync buttons on this page.",
                { ns: "Integrations" }
              )}
            </Alert>
          )}
          {isPaused && (
            <Alert
              severity="warning"
              sx={{ "& .MuiAlert-icon": { alignItems: "center" } }}
            >
              <AlertTitle>{t("Sync paused")}</AlertTitle>
              {ws?.pauseReason ?? t("Sync is paused. Controls are disabled.")}
            </Alert>
          )}
          {ws?.apiVersionStatus === "unsupported" && (
            <Alert severity="warning">
              <AlertTitle>{t("Unsupported API version")}</AlertTitle>
              {t("This PDS version may not work correctly.")}
            </Alert>
          )}

          {/* Initializing state */}
          {isInitializing && (
            <CardContainer title={t("Initializing...")}>
              <Stack spacing={2} sx={{ alignItems: "center", p: 4 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "First heartbeat is running. Dashboard will update automatically."
                  )}
                </Typography>
              </Stack>
            </CardContainer>
          )}

          {/* Main dashboard — only when initialized. Rendered even while the
              workflow is down: cards fall back to an inactive/empty state
              instead of disappearing, so the (still-enabled) trigger buttons
              stay reachable (MEIS-13089). */}
          {!isInitializing && (
            <>
              {/* Tier cards — side by side, Active fixed, Remaining fills */}
              <Stack direction="row" spacing={2} sx={{ alignItems: "stretch" }}>
                <Box sx={{ flex: 1, minWidth: 0, "& > *": { height: "100%" } }}>
                  <TierCard
                    title={t("Active customers")}
                    count={ws?.tier1Count ?? 0}
                    percentage={tier1Pct}
                    ringColor="#4caf50"
                    stats={tier1Stats}
                    progress={
                      ws?.tier1Running &&
                      ws.tier1TotalBatches > 0 &&
                      ws.tier1CompletedBatches < ws.tier1TotalBatches
                        ? ws.tier1CompletedBatches / ws.tier1TotalBatches
                        : undefined
                    }
                    onTrigger={() =>
                      handleMutation(() => triggerTier1(), setPendingTier1)
                    }
                    onCancel={() =>
                      handleMutation(
                        () => cancelTier1(),
                        setPendingCancelTier1,
                        { refetchAfter: true }
                      )
                    }
                    triggerLabel={t("Sync now", { ns: "Integrations" })}
                    cancelLabel={t("Cancel")}
                    triggerDisabled={isPaused}
                    isRunning={ws?.tier1Running ?? false}
                    pending={pendingTier1 || pendingCancelTier1}
                    warning={
                      ws && !ws.tier1LastCompletedAt && !ws.tier1Running
                        ? t("Never synced yet.")
                        : undefined
                    }
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, "& > *": { height: "100%" } }}>
                  <TierCard
                    title={t("Remaining customers")}
                    count={tier2Count}
                    percentage={tier2Pct}
                    ringColor="#1565C0"
                    stats={fullSyncStats}
                    onTrigger={() => setConfirmFullSyncOpen(true)}
                    onCancel={() =>
                      handleMutation(
                        () => cancelFullSync(),
                        setPendingCancelFullSync,
                        { refetchAfter: true }
                      )
                    }
                    triggerLabel={t("Start Full Sync")}
                    cancelLabel={t("Cancel")}
                    triggerDisabled={isPaused}
                    isRunning={ws?.fullSyncRunning ?? false}
                    pending={pendingFullSync || pendingCancelFullSync}
                    warning={
                      ws && !ws.fullSyncLastCompletedAt && !ws.fullSyncRunning
                        ? t("Never synced yet.")
                        : undefined
                    }
                  />
                </Box>
              </Stack>
            </>
          )}

          {/* Activity feed — always shown */}
          <ActivityFeed events={events} onRefresh={refetch} />

          {/* Sync controls — pause/resume, isolated from the header. Pause is
              running-only (disabled while the workflow is down); resume
              revives the sync via signalWithStart, so it stays enabled
              (MEIS-13089, D3). */}
          <CardContainer title={t("Sync controls", { ns: "Integrations" })}>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                px: 1,
                pt: 0.5,
                pb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("Pause or resume synchronization here.", {
                  ns: "Integrations",
                })}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color={isPaused ? "success" : "warning"}
                disabled={isWorkflowDown && !isPaused}
                onClick={() => setConfirmPauseToggleOpen(true)}
              >
                {isPaused ? t("Resume") : t("Pause")}
              </Button>
            </Stack>
          </CardContainer>
        </Stack>
      </PageContainer>

      {/* Full Sync re-syncs the entire dataset — confirm before triggering */}
      <Dialog
        open={confirmFullSyncOpen}
        onClose={() => setConfirmFullSyncOpen(false)}
      >
        <DialogTitle>{t("Start Full Sync?")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              "This resyncs the entire customer dataset, not just recent changes. It may take a while."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setConfirmFullSyncOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmFullSyncOpen(false);
              handleMutation(() => triggerFullSync(), setPendingFullSync);
            }}
          >
            {t("Start Full Sync")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pausing/resuming affects the whole sync — confirm before toggling */}
      <Dialog
        open={confirmPauseToggleOpen}
        onClose={() => setConfirmPauseToggleOpen(false)}
      >
        <DialogTitle>
          {isPaused
            ? t("Resume synchronization?", { ns: "Integrations" })
            : t("Pause synchronization?", { ns: "Integrations" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isPaused
              ? t("Synchronization will resume and controls will be enabled.")
              : t(
                  "Synchronization will pause and controls will be disabled until you resume."
                )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setConfirmPauseToggleOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmPauseToggleOpen(false);
              handleMutation(isPaused ? resume : pause);
            }}
          >
            {isPaused ? t("Resume") : t("Pause")}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};
